'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useStore } from 'utils/hooks/store';
import { getUrlParam } from 'containers/Tiptap/lib/tiptap-collab-utils';

export type AiContextValue = {
  aiToken: string | null;
  hasAi: boolean;
  setupError: boolean;
};

export const AiContext = createContext<AiContextValue>({
  hasAi: false,
  aiToken: null,
  setupError: false,
});

export const AiConsumer = AiContext.Consumer;
export const useAi = (): AiContextValue => {
  const context = useContext(AiContext);
  if (!context) {
    throw new Error('useAi must be used within an AiProvider');
  }
  return context;
};

export const useAiToken = (): AiContextValue => {
  const { session } = useStore();
  const [hasAi, setHasAi] = useState<boolean>(true);
  const [setupError, setSetupError] = useState<boolean>(false);

  const aiToken = session.user?.tiptapAiUserToken || null;

  useEffect(() => {
    const noAiParam = getUrlParam('noAi');
    setHasAi(parseInt(noAiParam || '0') !== 1);
  }, []);

  useEffect(() => {
    if (!hasAi) return;

    if (!aiToken) {
      setSetupError(true);
    }
  }, [hasAi, aiToken]);

  return { aiToken, hasAi, setupError };
};

export function AiProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>): React.JSX.Element {
  const { hasAi, aiToken, setupError } = useAiToken();

  const value = useMemo<AiContextValue>(
    () => ({
      hasAi,
      aiToken,
      setupError,
    }),
    [hasAi, aiToken, setupError]
  );

  return <AiContext.Provider value={value}>{children}</AiContext.Provider>;
}
